import type { CustomSkillEntity } from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  type Filter,
  FilterType,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import { getCustomSkillLevelId } from './customSkillTaxonomyRegistry';

type CustomSkillEntityLike = { id: string; levelId: number | null };

const getCustomSkillEntities = (
  filter: TreeFilter
): CustomSkillEntityLike[] => {
  return filter.selections.map((selection) => {
    const idSegments = selection.id.split('_');
    const lastIdSegment = idSegments[idSegments.length - 1] ?? selection.id;

    const labelLevelId = getCustomSkillLevelId(selection.levelId);
    const levelIndex = typeof labelLevelId === 'number' ? labelLevelId : null;

    return {
      id: lastIdSegment,
      levelId: levelIndex,
    };
  });
};

export const getCustomSkillFilter = (
  filter: Filter,
  effectiveFilters: Filter[]
) => {
  if (filter.type !== FilterType.TREE || filter.selections.length === 0) {
    return {};
  }

  // Find skill taxonomy filter to get the taxonomy ID
  let taxonomyId = 0; // default
  if (effectiveFilters.length > 0) {
    const skillTaxonomyFilter = effectiveFilters.find(
      (f) => f.id === 'SKILL_TAXONOMY'
    ) as SingleValueFilter<CustomSkillTaxonomySelection> | undefined;

    const valueObj = skillTaxonomyFilter?.value as
      | CustomSkillTaxonomySelection
      | undefined;
    if (valueObj && typeof valueObj.taxonomyId === 'number') {
      taxonomyId = valueObj.taxonomyId;
    }
  }

  return {
    custom_skill: {
      taxonomy_id: taxonomyId,
      entities: getCustomSkillEntities(filter as TreeFilter)
        .map((e) => {
          const entityId = Number(e.id);
          if (!Number.isInteger(entityId) || typeof e.levelId !== 'number') {
            return null;
          }
          return {
            entity_id: entityId,
            level_id: e.levelId,
          } as CustomSkillEntity;
        })
        .filter((e): e is CustomSkillEntity => e !== null),
    },
  };
};
